import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowSquareOutIcon as Out, XIcon as X, WarningIcon as Warning, PencilSimpleIcon as Pencil, ProhibitIcon as Prohibit } from "@phosphor-icons/react";
import { getScreenBinding } from "@/lib/api";
import BindingStatusBadge from "@/components/screenbindings/BindingStatusBadge";
import OriginBadge from "@/components/screenbindings/OriginBadge";
import TypeBadge from "@/components/fielddeps/TypeBadge";
import { isInactiveBinding, isManualBinding, operationLabel, statusMeta } from "@/lib/screenBindings";

function Row({ label, value, mono, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-[var(--ml-border)] last:border-b-0 text-xs">
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] shrink-0">{label}</span>
      <span className={`text-right break-words ${mono ? "ml-mono" : ""}`} data-testid={testid}>{value ?? <span className="text-[var(--ml-text-secondary)]">—</span>}</span>
    </div>
  );
}

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);

/** Iter 45 / 48 — detail drawer for one screen format binding (parser-proven or consultant-supplied). */
export default function BindingDetail({ workspaceId, bindingId, onClose, canWrite, onEdit, onDeactivate, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bindingId) return undefined;
    let alive = true;
    setData(null);
    setError(null);
    getScreenBinding(workspaceId, bindingId).then((d) => alive && setData(d)).catch((e) => alive && setError(e?.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [workspaceId, bindingId, refreshKey]);

  useEffect(() => {
    if (!bindingId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bindingId, onClose]);

  if (!bindingId) return null;
  const b = data?.binding;
  const manual = isManualBinding(b);
  const inactive = isInactiveBinding(b);
  return (
    <aside className="ml-card p-4 space-y-4 lg:sticky lg:top-4 self-start" data-testid="sb-detail" data-origin={b ? (manual ? "manual" : "parser") : undefined}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Screen format binding #{bindingId}</div>
          {b && <div className="text-lg font-semibold ml-mono mt-0.5" data-testid="sb-detail-title">{b.program_name} {operationLabel(b.operation)} {b.record_format}</div>}
        </div>
        <button type="button" onClick={onClose} className="p-1 hover:bg-[var(--ml-surface)]" data-testid="sb-detail-close" aria-label="Close"><X size={16} /></button>
      </div>
      {error && <p className="text-xs text-[#a2191f]" data-testid="sb-detail-error">{error}</p>}
      {!b && !error && <p className="text-xs text-[var(--ml-text-secondary)]">Loading…</p>}
      {b && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <OriginBadge origin={manual ? "manual" : "parser"} inactive={inactive} testid="sb-detail-origin" />
            <BindingStatusBadge status={b.status} confidence={b.confidence} testid="sb-detail-status" />
          </div>
          {manual && (
            <p className="text-xs px-3 py-2 flex items-start gap-2 bg-[#fff1f1] text-[#a2191f]" data-testid="sb-detail-manual-warning">
              <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
              <span>{data.manual_warning} {inactive ? "This binding is deactivated — it feeds nothing until reactivated." : ""}</span>
            </p>
          )}
          {b.ambiguity_reason && (
            <p className="text-xs px-3 py-2" style={{ background: statusMeta(b.status).bg, color: statusMeta(b.status).fg }} data-testid="sb-detail-ambiguity">
              {b.ambiguity_reason}
            </p>
          )}
          {manual && canWrite && (
            <div className="flex flex-wrap gap-2" data-testid="sb-detail-actions">
              <button type="button" onClick={() => onEdit?.(b)} className="ml-btn-secondary flex items-center gap-1.5" data-testid="sb-detail-edit-btn">
                <Pencil size={13} weight="bold" /> Edit manual binding
              </button>
              {!inactive && (
                <button type="button" onClick={() => onDeactivate?.(b)} className="ml-btn-secondary flex items-center gap-1.5 text-[#a2191f]" data-testid="sb-detail-deactivate-btn">
                  <Prohibit size={13} weight="bold" /> Deactivate
                </button>
              )}
            </div>
          )}
          <div>
            <Row label="Origin" value={manual ? "Manual — consultant supplied, not parser-proven" : "Parser-proven — observed RPG statement"} testid="sb-detail-origin-text" />
            <Row label="Program" value={`${b.program_library ? `${b.program_library}/` : ""}${b.program_name}`} mono testid="sb-detail-program" />
            <Row label="Source" value={b.source_file ? `${b.source_file}/${b.source_member || "?"}` : b.source_member} mono />
            <Row label="Display file" value={b.display_file || (b.status === "ambiguous" ? "ambiguous format name" : "unresolved format usage")} mono testid="sb-detail-display-file" />
            <Row label="Record format" value={b.record_format} mono testid="sb-detail-record-format" />
            <Row label="Operation" value={`${operationLabel(b.operation)} — program statement that ${b.operation === "exfmt" || b.operation === "write" ? "displays" : "reads"} the format`} testid="sb-detail-operation" />
            <Row label="Source line" value={b.source_line ?? (manual ? "none — consultant supplied, no source statement claimed" : null)} mono={!manual} testid="sb-detail-line" />
            <Row label="Evidence" value={b.evidence_source} mono={!manual} testid="sb-detail-evidence" />
            {manual && <Row label="Manual note" value={b.manual_note} testid="sb-detail-manual-note" />}
            {manual && <Row label="Created by" value={b.created_by ? `${b.created_by} · ${fmt(b.created_at)}` : null} testid="sb-detail-created-by" />}
            {manual && <Row label="Updated by" value={b.updated_by ? `${b.updated_by} · ${fmt(b.updated_at)}` : null} testid="sb-detail-updated-by" />}
            {manual && inactive && <Row label="Deactivated" value={fmt(b.deleted_at)} testid="sb-detail-deactivated" />}
            <Row label="Flows into field references" value={!data.flows_into_field_references ? "no" : data.inherited_program_references > 0 ? `yes — ${data.inherited_program_references} reference${data.inherited_program_references === 1 ? "" : "s"} carry ${b.program_name}${manual ? " via this manual binding" : ""}` : `eligible — but ${b.display_file}/${b.record_format} has no parsed DDS screen fields yet, so 0 references carry ${b.program_name}`} testid="sb-detail-flows-refs" />
            <Row label="Appears in screen walkthrough" value={data.appears_in_walkthrough ? `yes — step ${data.walkthrough_steps.map((s) => `#${s.sequence_number}${s.source_line == null ? " (no source line)" : ` line ${s.source_line}`}`).join(", ")}` : "no"} testid="sb-detail-in-walkthrough" />
            <Row label="Fingerprint" value={b.fingerprint?.slice(0, 12)} mono />
            {!manual && <Row label="Updated" value={fmt(b.updated_at)} />}
          </div>
          {data.resolves?.length > 0 && (
            <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="sb-detail-resolves">{data.resolves.join(" · ")}</p>
          )}
          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">{manual ? "Source excerpt" : "Source excerpt"}</div>
            {manual ? (
              <p className="text-[11px] ml-mono bg-[var(--ml-surface)] border border-[var(--ml-border)] p-2 text-[var(--ml-text-secondary)]" data-testid="sb-detail-excerpt">no source excerpt — manual decision, not parser-proven</p>
            ) : (
              <pre className="text-[11px] ml-mono bg-[var(--ml-surface)] border border-[var(--ml-border)] p-2 overflow-x-auto whitespace-pre" data-testid="sb-detail-excerpt">{b.source_excerpt || "—"}</pre>
            )}
          </div>
          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
              DSPF fields in {b.display_file ? `${b.display_file}/${b.record_format}` : "format"} ({data.dspf_fields.length})
            </div>
            {data.dspf_fields.length === 0 ? (
              <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="sb-detail-dspf-fields-empty">{b.display_file ? "No fields in this record format (or the display file / format is not in the parsed DDS)." : "No display file bound — fields cannot be listed."}</p>
            ) : (
              <div className="flex flex-wrap gap-1" data-testid="sb-detail-dspf-fields">
                {data.dspf_fields.map((f) => <span key={f} className="ml-mono text-[11px] px-1.5 py-0.5 border border-[var(--ml-border)]">{f}</span>)}
              </div>
            )}
          </div>
          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
              Linked field references ({data.field_references.length}) · {data.inherited_program_references} inherit {b.program_name}
            </div>
            {data.field_references.length === 0 ? (
              <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="sb-detail-refs-empty">None — rebuild field references after parsing the display file.</p>
            ) : (
              <ul className="text-xs space-y-0.5 max-h-56 overflow-y-auto" data-testid="sb-detail-refs">
                {data.field_references.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2" data-testid={`sb-detail-ref-${r.id}`} data-program={r.program_name || ""}>
                    <Link to={`/workspaces/${workspaceId}/metadata/field-dependencies?display_file=${encodeURIComponent(b.display_file)}&field=${encodeURIComponent(r.field_name)}`} className="ml-mono text-[var(--ml-primary)] hover:underline min-w-0 truncate">
                      {r.field_name}
                    </Link>
                    <span className="flex items-center gap-1 shrink-0">
                      <TypeBadge type={r.reference_type} />
                      <span className="ml-mono text-[10px] text-[var(--ml-text-secondary)]">{r.program_name || "no program"}{/manual screen format binding/.test(r.usage_context || "") ? " · manual" : ""}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            {data.appears_in_walkthrough && (
              <Link to={`/workspaces/${workspaceId}/metadata/screen-walkthrough?program=${encodeURIComponent(b.program_name)}&step=${data.walkthrough_steps[0].sequence_number}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sb-detail-walkthrough-link">
                Screen walkthrough step <Out size={11} />
              </Link>
            )}
            {data.source_fact && (
              <Link to={`/workspaces/${workspaceId}/metadata/source-parser?program=${encodeURIComponent(data.source_fact.program_name)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sb-detail-fact-link">
                Parsed {data.source_fact.source_type} facts <Out size={11} />
              </Link>
            )}
            {b.display_file && (
              <Link to={`/workspaces/${workspaceId}/metadata/source-parser?program=${encodeURIComponent(b.display_file)}`} className="text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1" data-testid="sb-detail-dspf-link">
                Parsed DDS facts <Out size={11} />
              </Link>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
